import { type LeadListItem } from '@utazzo/shared';
import { UzButton, UzCombobox, UzDialog, UzSpinner } from '@utazzo/ui';
import React, { useEffect, useMemo, useState } from 'react';

import { useAgentOptionsForLeads } from '../use-lead-options';
import { useBulkReassignLeads } from '../use-leads';

/**
 * Reassign the inbox aside's selection to one agent - the bulk form of the
 * header's "Assigned agent" pencil. Every rule is the server's (one PATCH-style
 * owner change per lead): the agent list is already hierarchy-scoped (a sales
 * lead sees self + direct reports), closed leads are left out here because the
 * server refuses them anyway, and anything else it refuses (pending Lost
 * approval, agent does not cover the destination) comes back per lead and is
 * listed below instead of failing the batch.
 */
export const LeadBulkReassignDialog = ({
  leads,
  onClose,
  onDone,
}: {
  /** The ticked rows; null = closed. */
  leads: ReadonlyArray<LeadListItem> | null;
  onClose: () => void;
  /** Every lead landed (or was already there) - the aside leaves select mode. */
  onDone: () => void;
}): React.JSX.Element => {
  const agents = useAgentOptionsForLeads();
  const assign = useBulkReassignLeads();
  const [agentId, setAgentId] = useState('');
  // Leads sent so far - the request goes out in chunks (bulkReassignLeads).
  const [done, setDone] = useState(0);

  useEffect(() => {
    setAgentId('');
    setDone(0);
    assign.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  const open = useMemo(() => (leads ?? []).filter((l) => l.currentBucket === 'OPEN'), [leads]);
  const closedCount = (leads?.length ?? 0) - open.length;
  const alreadyWithAgent = agentId ? open.filter((l) => l.assignedAgent?.id === agentId).length : 0;
  const count = open.length;
  const isSaving = assign.isPending;
  const failed = assign.data?.failed ?? [];
  const labelOf = new Map(
    open.map((l) => [l.id, `${l.customer.displayName} (${l.reference})`] as const),
  );
  const agentName = agents.options.find((o) => o.value === agentId)?.label;

  const handleSave = async (): Promise<void> => {
    if (!agentId || count === 0) return;
    // Branch follows the agent, exactly as the single picker sends it.
    const branchId = agents.branchByAgentId.get(agentId);
    try {
      const res = await assign.mutateAsync({
        ids: open.map((l) => l.id),
        assignedAgentId: agentId,
        ...(branchId !== undefined ? { branchId } : {}),
        onProgress: (n) => setDone(n),
      });
      if (res.failed.length === 0) onDone();
    } catch {
      // The mutation toasts the server's reason; keep the dialog open.
    }
  };

  return (
    <UzDialog
      open={leads !== null}
      // Locked while in flight: closing mid-request would hide whether it landed.
      forceOpen={isSaving}
      onOpenChange={(next) => !next && onClose()}
      title={`Assign ${count} lead${count === 1 ? '' : 's'}`}
      description="Move the selected leads to one agent. The new owner is notified, as with a single reassign."
    >
      <div className="space-y-3 py-2">
        {closedCount > 0 && (
          <p className="rounded-md bg-zinc-100 px-3 py-2 text-xs text-zinc-700">
            {closedCount} selected lead{closedCount === 1 ? ' is' : 's are'} Won or Lost, so{' '}
            {closedCount === 1 ? 'it is' : 'they are'} left out. Reopen a lead to reassign it.
          </p>
        )}
        {count > 0 ? (
          <UzCombobox
            label="Assign to"
            placeholder="Select an agent"
            searchPlaceholder="Search name…"
            value={agentId}
            onValueChange={setAgentId}
            options={agents.options}
            loading={agents.isLoading}
            disabled={isSaving}
            emptyText="No matches"
          />
        ) : (
          <p className="py-4 text-center text-sm text-zinc-500">
            None of the selected leads is open, so there is nothing to reassign.
          </p>
        )}
        {alreadyWithAgent > 0 && !isSaving && (
          <p className="text-xs text-zinc-500">
            {alreadyWithAgent === count
              ? `All of them are already with ${agentName ?? 'this agent'}.`
              : `${alreadyWithAgent} already with ${agentName ?? 'this agent'} - left as they are.`}
          </p>
        )}
        {isSaving && (
          <div className="rounded-md border border-blue-200 bg-blue-50/60 px-4 py-3">
            <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
              <span className="flex items-center gap-2 font-medium text-zinc-800">
                <UzSpinner size="xs" tone="primary" />
                Assigning leads…
              </span>
              <span className="text-zinc-600 tabular-nums">
                {done.toLocaleString('en-IN')} / {count.toLocaleString('en-IN')}
              </span>
            </div>
            {/* Fills per chunk sent; the shimmer track shows under the fill. */}
            <div className="skel h-2 w-full overflow-hidden rounded-full">
              <div
                className="h-full rounded-full bg-blue-600 transition-[width]"
                style={{ width: `${count > 0 ? Math.round((done / count) * 100) : 0}%` }}
              />
            </div>
            <div className="mt-1.5 text-[11px] text-zinc-500">
              Please keep this window open until the assignment finishes.
            </div>
          </div>
        )}
        {!isSaving && failed.length > 0 && (
          <div className="max-h-40 overflow-y-auto rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            <p className="font-medium">
              {assign.data!.updated} reassigned, {failed.length} could not be:
            </p>
            <ul className="mt-1 list-disc pl-4">
              {failed.map((f) => (
                <li key={f.id}>
                  {labelOf.get(f.id) ?? f.id}: {f.error}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <UzButton variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </UzButton>
          <UzButton
            onClick={() => void handleSave()}
            loading={isSaving}
            disabled={!agentId || count === 0 || isSaving}
          >
            {isSaving ? 'Assigning…' : 'Save assignment'}
          </UzButton>
        </div>
      </div>
    </UzDialog>
  );
};
